import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Play, Globe, ArrowRight } from "lucide-react";
import axios from "axios";
import { Seo } from "../components/Seo";

const API = "/api";
const SAFFRON = "#C99A2E";
const SAFFRON_LIGHT = "#F2E8DD";
const IVORY = "#FEFCF7";
const INK = "#1A1A18";
const INK_MID = "#3D3D38";
const INK_SOFT = "#6B6B63";

function getInitials(name) {
  return name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
}

function YouTubeEmbed({ url, title }) {
  let videoId = "";
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) videoId = u.pathname.slice(1);
    else videoId = u.searchParams.get("v") || "";
  } catch { videoId = ""; }
  if (!videoId) return null;

  return (
    <div className="relative w-full" style={{ borderRadius: '12px', overflow: 'hidden', aspectRatio: '16/9' }}>
      <iframe
        src={`https://www.youtube.com/embed/${videoId}`}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="w-full h-full"
        style={{ border: 'none' }}
      />
    </div>
  );
}

export default function IconProfilePage() {
  const { slug } = useParams();
  const [icon, setIcon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allIcons, setAllIcons] = useState([]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/icons/${slug}`),
      axios.get(`${API}/icons`),
    ]).then(([iconRes, allRes]) => {
      setIcon(iconRes.data);
      setAllIcons(allRes.data.filter(i => !i.is_anchor && i.slug !== slug));
    }).catch(() => {})
    .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: IVORY }}>
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: SAFFRON, borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (!icon) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ background: IVORY }}>
        <p className="text-lg" style={{ color: INK_MID }}>Icon not found</p>
        <Link to="/icons" className="text-sm underline" style={{ color: SAFFRON }}>Back to Icons</Link>
      </div>
    );
  }

  const hasPortrait = icon.portrait_url && icon.portrait_url.length > 5;
  const episodes = icon.episodes || [];
  const relatedIcons = allIcons.slice(0, 3);

  return (
    <div data-testid="icon-profile-page" className="min-h-screen" style={{ background: IVORY }}>
      <Seo
        title={`${icon.name} — Icons of India`}
        description={icon.bio_short || icon.title_line}
        path={`/icons/${icon.slug}`}
        image={hasPortrait ? icon.portrait_url : undefined}
        type="profile"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Person",
          name: icon.name,
          jobTitle: icon.title_line,
          description: icon.bio_short,
          image: hasPortrait ? icon.portrait_url : undefined,
          url: `https://iambecause.com/icons/${icon.slug}`,
          knowsAbout: icon.category,
        }}
      />
      {/* Back nav */}
      <div style={{ background: IVORY, borderBottom: '0.5px solid rgba(26,26,24,0.06)' }}>
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-3">
          <Link
            to="/icons"
            data-testid="back-to-icons"
            className="inline-flex items-center gap-2 text-sm transition-colors hover:opacity-80"
            style={{ color: INK_SOFT }}
          >
            <ArrowLeft size={16} /> Icons of India
          </Link>
        </div>
      </div>

      {/* Hero section — portrait + info */}
      <div style={{ background: icon.is_anchor ? `linear-gradient(135deg, ${INK} 0%, #2A2520 100%)` : `linear-gradient(160deg, ${IVORY} 0%, ${SAFFRON_LIGHT} 100%)` }}>
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-12 md:py-20">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-10 md:gap-16">
            {/* Portrait */}
            <div className="flex-shrink-0">
              {hasPortrait ? (
                <img
                  src={icon.portrait_url}
                  alt={icon.name}
                  data-testid="icon-portrait"
                  className="iab-bw-portrait w-44 h-44 md:w-56 md:h-56 rounded-full object-cover border-4"
                  style={{ borderColor: '#C9A24B' }}
                />
              ) : (
                <div
                  data-testid="icon-portrait-placeholder"
                  className="w-44 h-44 md:w-56 md:h-56 rounded-full flex items-center justify-center text-5xl font-medium border-4"
                  style={{ background: icon.is_anchor ? 'rgba(201,154,46,0.15)' : SAFFRON_LIGHT, color: SAFFRON, borderColor: SAFFRON, fontFamily: "var(--iab-font-accent)" }}
                >
                  {getInitials(icon.name)}
                </div>
              )}
            </div>

            {/* Text */}
            <div className="flex-1 text-center md:text-left">
              {/* Categories */}
              <div className="flex flex-wrap justify-center md:justify-start gap-2 mb-4">
                {icon.category && icon.category.map(cat => (
                  <span
                    key={cat}
                    className="px-3 py-1 rounded-full text-xs font-medium"
                    style={{ background: icon.is_anchor ? 'rgba(201,154,46,0.15)' : 'rgba(201,154,46,0.1)', color: SAFFRON }}
                  >
                    {cat}
                  </span>
                ))}
              </div>

              <h1
                data-testid="icon-name"
                className="text-3xl md:text-5xl font-medium mb-3"
                style={{ fontFamily: "'Playfair Display', Georgia, serif", color: icon.is_anchor ? '#FEFCF7' : INK, letterSpacing: '-0.5px', lineHeight: 1.15 }}
              >
                {icon.name}
              </h1>

              <p
                className="text-lg mb-6"
                style={{ fontFamily: "var(--iab-font-accent)", fontStyle: 'italic', color: icon.is_anchor ? 'rgba(254,252,247,0.65)' : SAFFRON, lineHeight: 1.6 }}
              >
                {icon.title_line}
              </p>

              {icon.website && (
                <a
                  href={icon.website}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-sm transition-opacity hover:opacity-80"
                  style={{ color: icon.is_anchor ? 'rgba(254,252,247,0.5)' : INK_SOFT }}
                >
                  <Globe size={14} /> {icon.website}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bio section */}
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="mb-2" style={{ height: '2px', width: '40px', background: SAFFRON, borderRadius: '2px' }} />
        <h2 className="text-xs uppercase tracking-widest font-semibold mb-6" style={{ color: SAFFRON }}>Biography</h2>
        <div style={{ fontFamily: "var(--iab-font-accent)", fontSize: '17px', lineHeight: 1.9, color: INK_MID }}>
          {icon.bio_full.split('\n\n').map((p, i) => (
            <p key={i} className="mb-5">{p}</p>
          ))}
        </div>
      </div>

      {/* "I am because" block */}
      {icon.i_am_because && (
        <div style={{ background: `linear-gradient(135deg, ${INK} 0%, #2A2520 100%)` }}>
          <div className="max-w-3xl mx-auto px-6 md:px-12 py-14 text-center">
            <p className="uppercase text-xs tracking-widest mb-6 font-semibold" style={{ color: SAFFRON }}>
              I am because...
            </p>
            <blockquote style={{ fontFamily: "var(--iab-font-accent)", fontSize: 'clamp(20px, 3vw, 28px)', lineHeight: 1.7, color: '#FEFCF7', fontStyle: 'italic' }}>
              "{icon.i_am_because}"
            </blockquote>
            <p className="mt-6 text-sm" style={{ color: 'rgba(254,252,247,0.45)' }}>— {icon.name}</p>
          </div>
        </div>
      )}

      {/* Episodes / Conversations */}
      {episodes.length > 0 && (
        <div style={{ background: '#fff', borderTop: '0.5px solid rgba(26,26,24,0.06)' }}>
          <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
            <div className="flex items-center justify-between mb-8">
              <div>
                <div className="mb-2" style={{ height: '2px', width: '40px', background: SAFFRON, borderRadius: '2px' }} />
                <h2 className="text-xs uppercase tracking-widest font-semibold" style={{ color: SAFFRON }}>Conversations</h2>
              </div>
              <span className="text-sm" style={{ color: INK_SOFT }}>{episodes.length} episode{episodes.length !== 1 ? 's' : ''}</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {episodes.map((ep, i) => (
                <div key={i} data-testid={`episode-${i}`} className="space-y-3">
                  {ep.youtube_url && <YouTubeEmbed url={ep.youtube_url} title={ep.title} />}
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: SAFFRON_LIGHT, color: SAFFRON }}>
                      {ep.episode_number || i + 1}
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold" style={{ color: INK }}>{ep.title}</h3>
                      {ep.description && <p className="text-xs mt-1" style={{ color: INK_SOFT }}>{ep.description}</p>}
                      {ep.duration && <p className="text-xs mt-1" style={{ color: INK_SOFT }}>{ep.duration}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Related Icons */}
      {relatedIcons.length > 0 && (
        <div style={{ background: SAFFRON_LIGHT, borderTop: '0.5px solid rgba(201,154,46,0.1)' }}>
          <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xs uppercase tracking-widest font-semibold" style={{ color: SAFFRON }}>More Icons</h2>
              <Link to="/icons" className="text-sm flex items-center gap-1 transition-opacity hover:opacity-80" style={{ color: INK_SOFT }}>
                View all <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {relatedIcons.map(ri => {
                const riHasPortrait = ri.portrait_url && ri.portrait_url.length > 5;
                return (
                  <Link
                    key={ri.id}
                    to={`/icons/${ri.slug}`}
                    className="group block bg-white rounded-xl overflow-hidden border transition-all hover:shadow-md"
                    style={{ borderColor: 'rgba(26,26,24,0.06)' }}
                  >
                    <div className="flex items-center gap-4 p-4">
                      {riHasPortrait ? (
                        <img src={ri.portrait_url} alt={ri.name} className="iab-bw-portrait w-12 h-12 rounded-full object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-full flex items-center justify-center text-sm font-medium" style={{ background: SAFFRON_LIGHT, color: SAFFRON }}>
                          {getInitials(ri.name)}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold truncate" style={{ color: INK }}>{ri.name}</h3>
                        <p className="text-xs truncate" style={{ color: INK_SOFT }}>{ri.title_line}</p>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
